import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../App";
import { X, Loader2, Send, AtSign } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";

const ThreadPanel = ({ message, onClose, onSendReply, channelId }) => {
  const { t, i18n } = useTranslation();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [selectedMentions, setSelectedMentions] = useState([]);
  
  const repliesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Fetch thread replies
  useEffect(() => {
    const fetchReplies = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(
          `/chat/channels/${channelId}/messages?parent_message_id=${message.id}&limit=100`
        );
        setReplies(response.data);
      } catch (error) {
        console.error("Failed to fetch replies:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (message?.id) {
      fetchReplies();
    }
  }, [message?.id, channelId]);

  // Auto-scroll to bottom
  useEffect(() => {
    repliesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [replies.length]);

  // Fetch users for mentions
  const fetchMentionUsers = async (query) => {
    try {
      const response = await apiClient.get("/users");
      const filtered = response.data.filter(u => 
        u.status !== "invited" &&
        u.name?.toLowerCase().includes(query.toLowerCase())
      );
      setMentionUsers(filtered.slice(0, 5));
    } catch (e) {}
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setReplyBody(value);
    
    const lastAtIndex = value.lastIndexOf("@");
    if (lastAtIndex !== -1) {
      const textAfterAt = value.slice(lastAtIndex + 1);
      const hasSpace = textAfterAt.includes(" ");
      if (!hasSpace && textAfterAt.length > 0) {
        fetchMentionUsers(textAfterAt);
        setShowMentions(true);
      } else if (textAfterAt.length === 0) {
        fetchMentionUsers("");
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = replyBody.lastIndexOf("@");
    const newBody = replyBody.slice(0, lastAtIndex) + `@${user.name} `;
    setReplyBody(newBody);
    setSelectedMentions(prev => [...prev, user]);
    setShowMentions(false);
    textareaRef.current?.focus();
  };

  const handleSendReply = async () => {
    if (!replyBody.trim()) return;
    
    setSending(true);
    try {
      const newReply = await onSendReply(replyBody, selectedMentions.map(u => u.id));
      if (newReply) {
        setReplies(prev => [...prev, newReply]);
      }
      setReplyBody("");
      setSelectedMentions([]);
    } catch (error) {
      // Error handled in parent
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  return (
    <div className="w-96 border-l border-border flex flex-col bg-background" data-testid="thread-panel">
      {/* Header */}
      <div className="h-14 border-b border-border px-4 flex items-center justify-between flex-shrink-0">
        <h3 className="font-semibold">Thread</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          {/* Original Message */}
          <div className="mb-4 pb-4 border-b border-border">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary flex-shrink-0">
                {message.user_name?.charAt(0).toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-semibold text-sm">{message.user_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(message.created_at).toLocaleString(i18n.language)}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{message.body}</p>
              </div>
            </div>
          </div>

          {/* Replies */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : replies.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No replies yet. Be the first to reply!
            </p>
          ) : (
            <div className="space-y-4">
              {replies.map((reply) => (
                <div key={reply.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {reply.user_name?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className="font-medium text-sm">{reply.user_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(reply.created_at).toLocaleTimeString(i18n.language, {
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{reply.body}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div ref={repliesEndRef} />
        </div>
      </ScrollArea>

      {/* Reply Composer */}
      <div className="border-t border-border p-3">
        {/* Selected Mentions */}
        {selectedMentions.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {selectedMentions.map((user) => (
              <span 
                key={user.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
              >
                @{user.name}
              </span>
            ))}
          </div>
        )}

        <div className="relative">
          {/* Mention Suggestions */}
          {showMentions && mentionUsers.length > 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-full bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50">
              {mentionUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => insertMention(user)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary text-left text-sm"
                >
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  {user.name}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2">
            <Textarea
              ref={textareaRef}
              value={replyBody}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Reply..."
              className="min-h-[36px] max-h-[100px] resize-none text-sm"
              rows={1}
            />
            <Button
              size="icon"
              className="h-9 w-9 flex-shrink-0"
              onClick={handleSendReply}
              disabled={!replyBody.trim() || sending}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreadPanel;
